'use client'
import { Teams } from '@glovo/organisation'
import { type FC, useEffect, useRef } from 'react'
import { useCourierChat } from '../useCourierChat'
import { useHelpCenter } from '../useHelpCenter'
import { CourierChatIframe } from './courier-chat-iframe/CourierChatIframe'
import { ChatBubble } from '@/help-center/ui/chat-bubble/ChatBubble'
import { HelpCenterIframe } from '@/help-center/ui/help-center-iframe/HelpCenterIframe'
import { useAuth } from '@/tools/auth'
import { useFwfToggles } from '@/tools/fwf-toggles/client'
import { useErrorHandling } from '@/tools/o11y/client'
import { withErrorBoundary } from '@/tools/o11y/client'

const AGENT_CHAT_POLLING_INTERVAL = 15000
const COURIER_CHAT_POLLING_INTERVAL = 25000
const DECREASED_COURIER_CHAT_POLLING_INTERVAL = 15000

enum ChatRoles {
  agent = 'AGENT',
  courier = 'COURIER',
}

type ChatRoleType = `${ChatRoles}`

const HelpCenterComponent: FC = () => {
  const { isLoggedIn } = useAuth()
  const { withErrorTracking, execWithErrorTracking } = useErrorHandling()
  const { toggles } = useFwfToggles()
  const isCourierChatEnabled = toggles['lc_fe_dh_courier_chat_enabled']

  const {
    fetchChatState: fetchAgentChatState,
    chat: agentChat,
    isHelpCenterVisible,
  } = useHelpCenter()

  const { fetchCourierChatState, chats: courierChats, isCourierChatVisible } = useCourierChat()

  const hasActiveAgentChat = agentChat.active
  const hasCourierChat = courierChats.length > 0
  const hasActiveCourierChat = courierChats.some((chat) => chat.active)

  const isChatBubbleVisible =
    isLoggedIn &&
    !isHelpCenterVisible &&
    !isCourierChatVisible &&
    (hasActiveAgentChat || hasActiveCourierChat)

  const agentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const courierTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMounted = useRef(false)

  const pollAgentChat = async () => {
    if (!isMounted.current) {
      return
    }
    stopPolling(ChatRoles.agent)
    await fetchAgentChatState()
    // Only poll agent chat while has active conversation
    if (!hasActiveAgentChat) {
      stopPolling(ChatRoles.agent)
    } else {
      scheduleNextPoll(ChatRoles.agent, AGENT_CHAT_POLLING_INTERVAL)
    }
  }

  const pollCourierChat = async () => {
    if (!isMounted.current) {
      return
    }
    stopPolling(ChatRoles.courier)
    await fetchCourierChatState()
    // Courier chat is continually polled
    if (hasCourierChat) {
      scheduleNextPoll(ChatRoles.courier, DECREASED_COURIER_CHAT_POLLING_INTERVAL)
    } else {
      scheduleNextPoll(ChatRoles.courier, COURIER_CHAT_POLLING_INTERVAL)
    }
  }

  const scheduleNextPoll = (chat: ChatRoleType, interval: number) => {
    if (isMounted.current) {
      if (chat === ChatRoles.agent) {
        agentTimerRef.current = setTimeout(() => pollAgentChat(), interval)
      } else {
        courierTimerRef.current = setTimeout(() => pollCourierChat(), interval)
      }
    }
  }

  const stopPolling = (chat: ChatRoleType) => {
    if (chat === ChatRoles.agent && agentTimerRef.current) {
      clearTimeout(agentTimerRef.current)
      agentTimerRef.current = null
    } else if (chat === ChatRoles.courier && courierTimerRef.current) {
      clearTimeout(courierTimerRef.current)
      courierTimerRef.current = null
    }
  }

  // Poll agent chat when help center closed to check if still has active conversation
  useEffect(() => {
    execWithErrorTracking(() => {
      if (!isHelpCenterVisible) {
        void pollAgentChat()
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHelpCenterVisible])

  // Start polling on mount or on login
  useEffect(() => {
    execWithErrorTracking(() => {
      isMounted.current = true
      if (!isLoggedIn) {
        stopPolling(ChatRoles.agent)
        stopPolling(ChatRoles.courier)
      } else {
        void pollAgentChat()

        if (isCourierChatEnabled) {
          void pollCourierChat()
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isCourierChatEnabled])

  useEffect(
    // eslint-disable-next-line o11y-guard/error-tracking
    () =>
      withErrorTracking(() => {
        isMounted.current = false
        stopPolling(ChatRoles.agent)
        stopPolling(ChatRoles.courier)
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return (
    <div>
      {isHelpCenterVisible && <HelpCenterIframe />}
      {isCourierChatVisible && <CourierChatIframe />}
      {isChatBubbleVisible && <ChatBubble />}
    </div>
  )
}

export const HelpCenter = withErrorBoundary(HelpCenterComponent, {
  id: 'help_center.root',
  owner: Teams.WEBX,
})
